import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { AuthLoginService } from 'src/app/page/auth-page/service/auth-login.service';
import { serviceTypeInterface } from 'src/app/util/interfaces/requestClassification';
import { environment } from 'src/environments/environment';
import { CategoryCategorization, ScopeCategorization, TopicCategorization } from './request-categorization.interface';

@Injectable({
	providedIn: 'root',
})
export class RequestCategorizationService {
	private readonly http = inject(HttpClient);
	private readonly authLoginService = inject(AuthLoginService);

	private readonly getServiceTypesUrl = environment.baseApiUrl + 'register/request/serviceTypes';
	private readonly getSolutionAreasUrl = environment.baseApiUrl + 'register/request/solutionAreas';
	private readonly getCategorizationUrl = environment.baseApiUrl + 'register/request/categorization';
	private readonly scopeUrl = environment.baseApiUrl + 'register/request/categorization/scope';
	private readonly categoryUrl = environment.baseApiUrl + 'register/request/categorization/category';
	private readonly topicUrl = environment.baseApiUrl + 'register/request/categorization/topic';
	constructor() {}

	getServiceTypes(): Observable<serviceTypeInterface[]> {
		return this.http.get<serviceTypeInterface[]>(this.getServiceTypesUrl, { headers: this.authLoginService.getHttpHeaders() });
	}

	getSolutionAreas(): Observable<serviceTypeInterface[]> {
		return this.http.get<serviceTypeInterface[]>(this.getSolutionAreasUrl, { headers: this.authLoginService.getHttpHeaders() });
	}

	getCategorization(idServiceType: string, idSolutionArea?: string): Observable<ScopeCategorization[]> {
		const params: Record<string, string | boolean> = { idServiceType, showDeactivated: true };
		if (idSolutionArea) {
			params['idSolutionArea'] = idSolutionArea;
		}
		return this.http.get<ScopeCategorization[]>(this.getCategorizationUrl, {
			headers: this.authLoginService.getHttpHeaders(),
			params,
		});
	}

	createScope(scope: ScopeCategorization): Observable<ScopeCategorization> {
		const body = {
			scope: scope.name,
			mandatory: scope.mandatoryCiBinding,
			first_level: scope.firstLevel,
			monitoring: scope.monitoring,
			active: 1,
		};
		return this.http.post<ScopeCategorization>(this.scopeUrl, body, { headers: this.authLoginService.getHttpHeaders() });
	}

	patchScope(scope: ScopeCategorization): Observable<any> {
		const body = {
			idScope: scope.id,
			scope: scope.name,
			mandatory: scope.mandatoryCiBinding,
			first_level: scope.firstLevel,
			monitoring: scope.monitoring,
			active: scope.active,
		};
		return this.http.patch<any>(this.scopeUrl, body, { headers: this.authLoginService.getHttpHeaders() });
	}

	deleteScope(scope: ScopeCategorization): Observable<any> {
		const parameters = new HttpParams().set('idScope', scope.id);
		return this.http.delete<any>(this.scopeUrl, { headers: this.authLoginService.getHttpHeaders(), params: parameters });
	}

	createCategory(
		category: CategoryCategorization,
		scope: ScopeCategorization,
		idServiceType: string
	): Observable<CategoryCategorization> {
		const body = {
			active: 1,
			category: category.name,
			idScope: scope.id,
			idServiceType: idServiceType,
		};
		return this.http.post<CategoryCategorization>(this.categoryUrl, body, { headers: this.authLoginService.getHttpHeaders() });
	}

	patchCategory(category: CategoryCategorization): Observable<any> {
		const body = {
			idCategory: category.id,
			category: category.name,
			active: category.active,
		};
		return this.http.patch<any>(this.categoryUrl, body, { headers: this.authLoginService.getHttpHeaders() });
	}

	deleteCategory(category: CategoryCategorization): Observable<any> {
		const parameters = new HttpParams().set('idCategory', category.id);
		return this.http.delete<any>(this.categoryUrl, { headers: this.authLoginService.getHttpHeaders(), params: parameters });
	}

	createTopic(topic: TopicCategorization, category: CategoryCategorization, idSolutionArea?: string): Observable<TopicCategorization> {
		const body: Record<string, unknown> = {
			active: 1,
			topic: topic.name,
			idCategory: category.id,
		};
		if (topic.sla !== null && topic.sla !== undefined) {
			body['sla'] = topic.sla;
			body['idSolutionArea'] = idSolutionArea;
		}
		return this.http.post<TopicCategorization>(this.topicUrl, body, { headers: this.authLoginService.getHttpHeaders() });
	}

	patchTopic(topic: TopicCategorization, category: CategoryCategorization, idSolutionArea?: string): Observable<any> {
		const body: Record<string, unknown> = {
			idTopic: topic.id,
			topic: topic.name,
			active: topic.active,
		};
		if (topic.sla !== null && topic.sla !== undefined) {
			body['sla'] = topic.sla;
			body['idCategory'] = category.id;
			body['idSolutionArea'] = idSolutionArea;
			if (topic.idSla) {
				body['idSla'] = topic.idSla;
			}
		}
		return this.http.patch<any>(this.topicUrl, body, { headers: this.authLoginService.getHttpHeaders() });
	}

	deleteTopic(topic: TopicCategorization): Observable<any> {
		const parameters = new HttpParams().set('idTopic', topic.id);
		return this.http.delete<any>(this.topicUrl, { headers: this.authLoginService.getHttpHeaders(), params: parameters });
	}

	saveBulkCategorization(categorization: ScopeCategorization[], idServiceType: string): Observable<any> {
		const body = {
			scopes: [],
			categories: [],
			topics: [],
		};

		for (const scope of categorization) {
			body.scopes.push({
				idScope: scope.id,
				scope: scope.name,
				mandatory: scope.mandatoryCiBinding,
				first_level: scope.firstLevel,
				monitoring: scope.monitoring,
				active: scope.active,
			});
			if (scope.categories)
				for (const category of scope.categories) {
					body.categories.push({
						idCategory: category.id,
						category: category.name,
						idScope: scope.id,
						idServiceType: idServiceType,
						active: category.active,
					});
					if (category.topics)
						for (const topic of category.topics) {
							body.topics.push({
								idTopic: topic.id,
								topic: topic.name,
								idCategory: category.id,
								active: topic.active,
							});
						}
				}
		}
		return this.http.post<any>(this.getCategorizationUrl, body, {
			headers: this.authLoginService.getHttpHeaders(),
			params: { idServiceType },
		});
	}
}
